import Paths from '@/configs/router/paths';
import { userStorage } from '@/configs/storages/browser';
import URLs from '@/scripts/request/URLs';

export const googleLoginWeb = () => {
  const redirect_uri = isDesktop()
    ? `${import.meta.env.VITE_LOCAL_URL}${Paths.AUTH_CALLBACK}`
    : `${import.meta.env.VITE_LOCAL_URL}${Paths.LOGIN}`;
  const url = new URL(`https://accounts.google.com/o/oauth2/v2/auth`);
  url.searchParams.set('client_id', import.meta.env.VITE_CLIENT_ID_GOOGLE);
  url.searchParams.set('redirect_uri', redirect_uri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set(
    'scope',
    'openid email profile'
    // 'openid email profile https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.metadata.readonly https://www.googleapis.com/auth/drive'
  );
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');

  window.location.href = url.toString();
};

export const getGoogleUserInfo = async (code: string) => {
  const handleIDToken = async (credential: string) => {
    try {
      const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${credential}` },
      }).then((res) => res.json());
      return userInfo;
    } catch (error) {
      console.error(error);
    }
  };

  const exchangeCode = async (code: string) => {
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          code,
          client_id: import.meta.env.VITE_CLIENT_ID_GOOGLE,
          client_secret: import.meta.env.VITE_SECRET_GOOGLE,
          redirect_uri: `${import.meta.env.VITE_LOCAL_URL}/login`,
          grant_type: 'authorization_code',
        }),
      });
      const tokens = await response.json();

      return await handleIDToken(tokens.access_token);
    } catch (error) {
      console.error('Token exchange error:', error);
      throw error;
    }
  };

  return exchangeCode(code);
};

export const googleDriveAPI = {
  uploadFile: async (access_token: string, file: File) => {
    const metadata = {
      name: file.name,
      mimeType: file.type,
    };

    const form = new FormData();
    form.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );
    form.append('file', file);

    try {
      const response = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
          body: form,
        }
      );
      return await response.json();
    } catch (error) {
      console.error('Error uploading file to Google Drive:', error);
      throw error;
    }
  },

  listFiles: async (access_token: string) => {
    try {
      const response = await fetch(
        'https://www.googleapis.com/drive/v3/files?' +
          new URLSearchParams({
            fields: 'files(id,name,mimeType,size,modifiedTime)',
            spaces: 'drive',
            pageSize: '100',
          }),
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error listing Google Drive files:', error);
      throw error;
    }
  },

  downloadFile: async (access_token: string, fileId: string) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            Accept: 'application/json',
          },
        }
      );
      return await response.blob();
    } catch (error) {
      console.error('Error downloading file from Google Drive:', error);
      throw error;
    }
  },

  deleteFile: async (access_token: string, fileId: string) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );
      return response.ok;
    } catch (error) {
      console.error('Error deleting file from Google Drive:', error);
      throw error;
    }
  },
};

export const appleLoginWeb = () => {
  const redirect_web_uri = isDesktop()
    ? `${import.meta.env.VITE_LOCAL_URL}${Paths.AUTH_CALLBACK}`
    : `${import.meta.env.VITE_LOCAL_URL}${Paths.LOGIN}`;
  const apiUrl = new URLs('/api/v1/user/login/apple/callback');
  const url = new URL(`https://appleid.apple.com/auth/authorize`);
  url.searchParams.set('client_id', import.meta.env.VITE_CLIENT_ID_APPLE);
  url.searchParams.set('redirect_uri', apiUrl.href);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('response_mode', 'form_post');
  url.searchParams.set('scope', 'email name');
  url.searchParams.set('state', encodeURIComponent(redirect_web_uri));

  window.location.href = url.toString();
};

export const getDeviceId = () => {
  const storageKey = 'device_id';
  let deviceId = userStorage.getItem(storageKey);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    userStorage.setItem(storageKey, deviceId);
  }
  return deviceId;
};

export const getDeviceType = () => {
  const platform = navigator.platform.toLowerCase();
  if (location.protocol.includes('http')) {
    return 3;
  }
  if (platform.includes('win')) {
    return 4;
  }
  if (platform.includes('mac')) {
    return 5;
  }
  return 3;
};

export const isDesktop = () => {
  return typeof (window as any).chrome?.send !== 'undefined';
};
